import * as Sentry from '@sentry/vue'
import { reportScriptLoadError } from '@/utils/reportScriptLoadError'
import { COOKIE_OPTIONS } from '@/utils/constants/cookies'
import { EMARSYS_PUSH_RETRY_AT_STORAGE_KEY, EMARSYS_PUSH_RETRY_INTERVAL_MS } from '@/utils/constants/emarsys'
import { trackCookieAcceptEvent } from '@/utils/sap'

declare global {
  interface Window {
    WebEmarsysSdk?: any
  }
}

export default defineNuxtPlugin(() => {
  const {
    public: { sapDebug, sapAppCode, sapDomain, sapPublicKey, sapLoginFieldID, btAssets }
  } = useRuntimeConfig()

  if (!sapAppCode || !sapDomain || !sapPublicKey || !sapLoginFieldID || !btAssets) {
    console.warn('Emarsys configuration is not complete')
    return
  }

  const gdprCookies = useCookie<string>('gdpr_settings', { ...COOKIE_OPTIONS, watch: true })

  const hasConsent = () => {
    const raw = gdprCookies.value || ''
    const parts = new Set(
      raw
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
    )
    return parts.has('web') || parts.has('adv')
  }

  let consentGrantedOnPage = false
  const pushRetryAt = useLocalStorage(EMARSYS_PUSH_RETRY_AT_STORAGE_KEY, 0, { flush: 'sync' })

  globalThis.window.WebEmarsysSdk = globalThis.window.WebEmarsysSdk || []

  const registerDebugEvents = () => {
    globalThis.window.WebEmarsysSdk!.push(
      [
        'onSubscribe',
        (e: any) => {
          console.log('onSubscribe', e)
        }
      ],
      [
        'onUnsubscribe',
        (e: any) => {
          console.log('onUnsubscribe', e)
        }
      ],
      [
        'onPermissionPrompt',
        (e: any) => {
          console.log('onPermissionPrompt', e)
        }
      ],
      [
        'onPermissionGranted',
        (e: any) => {
          console.log('onPermissionGranted', e)
        }
      ],
      [
        'onPermissionDenied',
        (e: any) => {
          console.log('onPermissionDenied', e)
        }
      ],
      [
        'onSWInitError',
        (e: any) => {
          console.error('[Emarsys] onSWInitError', e)
        }
      ]
    )
  }

  const reportPushIssue = (message: string, extra: Record<string, unknown> = {}) => {
    Sentry.captureMessage(message, {
      level: 'warning',
      tags: { integration: 'emarsys-web-sdk' },
      extra
    })
  }

  const shouldSubscribeToPush = async (sdk: { isSubscribed: () => Promise<boolean> }) => {
    const { permission } = Notification
    if (permission === 'default') return true
    if (permission !== 'granted') return false
    if (consentGrantedOnPage) return true

    return Date.now() >= pushRetryAt.value && !(await sdk.isSubscribed())
  }

  const subscribeToPush = async () => {
    if (globalThis.Notification === undefined) return

    const sdk = globalThis.window.WebEmarsysSdk!
    try {
      if (!(await shouldSubscribeToPush(sdk))) return

      pushRetryAt.value = Date.now() + EMARSYS_PUSH_RETRY_INTERVAL_MS
      await sdk.subscribe()

      if (Notification.permission === 'granted' && !(await sdk.isSubscribed())) {
        reportPushIssue('emarsys_push_registration_incomplete')
      }
    } catch (error) {
      console.warn('[Emarsys] Push subscription failed', error)
      reportPushIssue('emarsys_push_subscribe_failed', {
        errorName: error instanceof Error ? error.name : typeof error
      })
    }
  }

  const onSdkReady = async () => {
    await subscribeToPush()
    await trackCookieAcceptEvent(gdprCookies.value || '')
  }

  const initSdk = () => {
    const config = {
      enableLogging: sapDebug,
      applicationCode: sapAppCode,
      enableMacSafariVapid: true,
      safariWebsitePushID: sapDomain,
      defaultNotificationTitle: 'Where will you go next?',
      defaultNotificationIcon: `${btAssets}/logo/airBaltic_logo_192x192.jpg`,
      autoSubscribe: false,
      serviceWorker: {
        url: 'service-worker.js',
        applicationServerPublicKey: sapPublicKey
      }
    }

    if (sapDebug) {
      registerDebugEvents()
      console.debug('[Emarsys] SDK Config:', config)
    }

    globalThis.window.WebEmarsysSdk!.push(['init', config])
    globalThis.window.WebEmarsysSdk!.push(onSdkReady)
  }

  const emarsysScriptSrc = 'https://client-version.cf.emarsys.net/web-emarsys-sdk-v4/latest/web-emarsys-sdk.js'

  const { scriptTag, load, unload } = useScriptTag(
    emarsysScriptSrc,
    () => {
      initSdk()
    },
    { manual: true, attrs: { id: 'emarsys-sdk' } }
  )

  const removeScriptAndReset = async () => {
    const sdk = globalThis.window.WebEmarsysSdk
    if (sdk && !Array.isArray(sdk)) await sdk.unsubscribe()

    if (document.getElementById('emarsys-sdk')) unload()
    globalThis.window.WebEmarsysSdk = []
  }

  const initWithConsent = async () => {
    if (hasConsent()) {
      if (scriptTag.value) {
        await trackCookieAcceptEvent(gdprCookies.value || '')
        return
      }

      try {
        await load()
      } catch (error) {
        reportScriptLoadError({
          error,
          integration: 'emarsys-web-sdk',
          message: 'emarsys_sdk_script_load_failed',
          scriptSrc: emarsysScriptSrc
        })
        await removeScriptAndReset()
      }
    } else {
      await removeScriptAndReset()
    }
  }

  initWithConsent()
  watch(gdprCookies, () => {
    consentGrantedOnPage = hasConsent()
    initWithConsent()
  })
})
